"use client";

import * as React from "react";

import { BrokerPanel } from "@/components/broker/broker-panel";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionPrimitive,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/ui/link";
import { cn } from "@/lib/utils";

interface BrokerFaqQuestion {
  question: string;
  answer: string;
}

interface BrokerFaqProps {
  title: string;
  description: string;
  cta: {
    label: string;
    href: string;
    external?: boolean;
  };
  questions: BrokerFaqQuestion[];
  className?: string;
}

export function BrokerFaq({
  title,
  description,
  cta,
  questions,
  className,
}: BrokerFaqProps) {
  const [openValue, setOpenValue] = React.useState<string | undefined>(
    questions[0]?.question
  );

  return (
    <BrokerPanel
      dataTestId="broker-faq-section"
      connectors="both"
      innerClassName="py-10 md:p-12 lg:p-16"
      className={cn("py-8 md:py-8 lg:py-20", className)}
    >
      <div className="flex flex-col gap-10 md:gap-16 lg:flex-row lg:gap-28">
        <div className="flex max-w-[260px] flex-col gap-4">
          <h2 className="text-ds-heading-h3 text-grey-950 text-balance">
            {title}
          </h2>
          <p className="text-ds-body-sm text-grey-600">{description}</p>
          <Button
            size="sm"
            className="w-fit"
            render={<Link href={cta.href} external={cta.external} />}
          >
            {cta.label}
          </Button>
        </div>

        <Accordion
          value={openValue ? [openValue] : []}
          onValueChange={(value) => setOpenValue(value[0])}
          className="flex flex-1 flex-col gap-4"
        >
          {questions.map((q) => {
            const open = openValue === q.question;
            return (
              <AccordionItem
                key={q.question}
                value={q.question}
                className="border-grey-100 bg-grey-50 rounded-ds-xl flex flex-col gap-4 border p-6"
              >
                <AccordionPrimitive.Header className="flex">
                  <AccordionPrimitive.Trigger
                    className={cn(
                      "text-grey-950 text-ds-body-md flex w-full items-center gap-4 text-left outline-none",
                      open && "py-1"
                    )}
                  >
                    <span className="flex-1">{q.question}</span>
                    <span
                      aria-hidden
                      className="border-grey-100 bg-grey-0 text-grey-950 flex size-7 shrink-0 items-center justify-center rounded-full border"
                    >
                      {open ? <MinusIcon /> : <PlusIcon />}
                    </span>
                  </AccordionPrimitive.Trigger>
                </AccordionPrimitive.Header>
                <AccordionContent className="pb-0">
                  <p className="text-grey-950 text-ds-body-md whitespace-pre-line">
                    {q.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </BrokerPanel>
  );
}

function PlusIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M6 2.5C6.27614 2.5 6.5 2.72386 6.5 3V5.5H9C9.27614 5.5 9.5 5.72386 9.5 6C9.5 6.27614 9.27614 6.5 9 6.5H6.5V9C6.5 9.27614 6.27614 9.5 6 9.5C5.72386 9.5 5.5 9.27614 5.5 9V6.5H3C2.72386 6.5 2.5 6.27614 2.5 6C2.5 5.72386 2.72386 5.5 3 5.5H5.5V3C5.5 2.72386 5.72386 2.5 6 2.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M9 5.5C9.27614 5.5 9.5 5.72386 9.5 6C9.5 6.27614 9.27614 6.5 9 6.5H3C2.72386 6.5 2.5 6.27614 2.5 6C2.5 5.72386 2.72386 5.5 3 5.5H9Z"
        fill="currentColor"
      />
    </svg>
  );
}
